import { SEO } from '@/features/SEO';
import { ContentContainer } from '@/features/shared';
import { MuiContainer } from '@/features/shared/material-ui';
import { ColorNames, InsetTop, SpacingSizes } from '@/utils/constants';
import { getDocumentBasedImage, parseHTMLString } from '@/utils/helpers';
import clsx from 'clsx';
import React, { memo, useMemo } from 'react';
import TechParnerCTA from '../shared/CTA/CTA';
import { MainContent } from '../shared/MainContent';
import { IntroContent } from './IntroContent';
import { RelatedSolution } from './RelatedSolution';
import { SolutionDetailProps } from './SolutionDetail.interface';
import useStyles from './SolutionDetail.styles';

const SolutionDetail = ({
  backgroundBannerImage,
  button,
  displayTags,
  backgroundBannerVideo,
  cta,
  headline,
  summary,
  includeBreadcrumb,
  includeJumplinks,
  logo,
  mobileBackgroundBanner,
  pageMetadata,
  shortDescription,
  tags,
  labels,
  detailContent,
  contentButtons,
  taxonomyList = {},
  relatedSolutions,
  relatedSolutionsTitle,
  relatedSolutionsButton,
  cardTheme,
  cardColor,
  logo_genetec_light,
  cardImage = {},
  tagLinks,
}: Br.PropsWithContentButtons<SolutionDetailProps>) => {
  const HAS_CTA = useMemo(
    () => JSON.stringify(cta?.ctaContent?.content || '').length > 2,
    [cta?.ctaContent?.content],
  );
  const classes = useStyles({});
  const containerProps = useMemo(
    () => ({
      backgroundImage: backgroundBannerImage,
      mobileBackgroundImage: mobileBackgroundBanner || backgroundBannerImage,
      backgroundVideo: backgroundBannerVideo,
      bottomBackgroundColor: ColorNames.eggshell,
      bottomSpacing: SpacingSizes.small,
      topBackgroundColor: backgroundBannerVideo ? 'transparent' : ColorNames.eggshell,
      topSpacing: SpacingSizes.small,
      offsetTop: true,
      insetTop: InsetTop.hero,
    }),
    [backgroundBannerImage, backgroundBannerVideo, mobileBackgroundBanner],
  );

  const introProps = {
    displayTags,
    button,
    headline,
    includeBreadcrumb,
    logo,
    labels,
    shortDescription,
    summary,
    tags,
    includeJumplinks,
    taxonomyList,
    tagLinks,
  };

  const mainProps = { detailContent };

  const hasRelateContent = Boolean(relatedSolutions?.length);
  const bgRelatedContent = ColorNames.darkGray;

  const relatedContentProps = {
    cardTheme,
    curatedList: {
      listTitle: relatedSolutionsTitle || 'Related Solutions',
      contentList: relatedSolutions,
      cta: relatedSolutionsButton,
    },
    presentation: {
      topSpacing: SpacingSizes.small,
      bottomSpacing: SpacingSizes.small,
      topBackgroundColor: ColorNames.darkGray,
      bottomBackgroundColor: ColorNames.darkGray,
    },
    cardColor,
    labels,
    config: {
      logo_genetec_light,
    },
  };

  const metadata = {
    ...pageMetadata,
    metaTitle: pageMetadata?.metaTitle || headline,
    description: pageMetadata?.description || parseHTMLString(shortDescription),
    socialMediaImage: getDocumentBasedImage({ pageMetadata, cardImage }),
  };

  const rootClasses = clsx(classes.root);

  return (
    <article id="tpd-solutions" className={rootClasses}>
      <ContentContainer className={classes.container} {...containerProps}>
        <MuiContainer className="large">
          {contentButtons?.content}
          <IntroContent {...introProps} />
        </MuiContainer>
      </ContentContainer>
      <section>
        <MainContent {...mainProps} />

        {hasRelateContent && (
          <ContentContainer
            topSpacing={SpacingSizes.small}
            bottomSpacing={SpacingSizes.medium}
            topBackgroundColor={bgRelatedContent}
            bottomBackgroundColor={bgRelatedContent}
          >
            <MuiContainer className="standard">
              <section>
                <RelatedSolution {...relatedContentProps} />
              </section>
            </MuiContainer>
          </ContentContainer>
        )}
      </section>
      {HAS_CTA && <TechParnerCTA cta={cta} />}
      {metadata && <SEO metadata={metadata} isDocumentBasedPage />}
    </article>
  );
};

export default memo(SolutionDetail);
